// NOTABLE HISTORY · 활동 이력 — 자동 마일스톤 + 수동 항목 merge.
//
// 구성(proposal §2-1 ②):
//   · 연도 없는 자동 마일스톤(구독자 돌파=달성 / 상위 N%=현재) → 상단 배지 그룹.
//   · 연도 있는 항목(자동 마일스톤 + 수동 어워드) → 연도 내림차순으로 하나의 타임라인에 merge.
//   · 마일스톤 미달성 + 수동 없음 = 빈 상태(임의 mock 금지).
// 수동 항목은 **포털 Mongo**가 원천이다(cic 무관) — 이 컴포넌트가 직접 조회·편집한다.
//   크리에이터 id는 워크스페이스 라우트(`/creators/:creatorId`)에서 읽는다. 부모(ProfileSection)에
//   prop을 새로 뚫지 않아, 같은 파일을 만지는 병렬 작업과 충돌하지 않는다.
//   조회 실패는 조용히 무시한다 — 자동 마일스톤(본문 데이터)은 그대로 보여야 하므로.
import { useCallback, useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { Pencil, Plus, Trash2, X } from 'lucide-react'
import type { DataMeta } from '../../creatorsApi'
import {
  createNotableHistory,
  deleteNotableHistory,
  fetchNotableHistory,
  updateNotableHistory,
  type NotableHistoryEntry,
  type NotableHistoryInput,
} from '../../notableHistoryApi'
import { MetricCaption, resolveMetric } from '../metric'
import { PLATFORM_LABEL, buildBasisNote } from '../overview/format'
import { mergeHistoryRows, type MergedHistory } from './historyRows'
import { NotableHistoryEditor } from './NotableHistoryEditor'
import { SectionHeader } from './SectionHeader'

export function NotableHistory({
  history,
  meta,
}: {
  /** 전 채널 합본. 채널마다 갈리는 계단은 줄마다 `sourcePlatform` 을 달고 온다(historyRows.ts). */
  history: MergedHistory
  meta?: DataMeta
}) {
  const { creatorId } = useParams<{ creatorId: string }>()
  const { milestones, manual } = history
  const [entries, setEntries] = useState<NotableHistoryEntry[]>([])
  const [editing, setEditing] = useState<'off' | 'on'>('off')
  const [adding, setAdding] = useState(false)
  const [editId, setEditId] = useState<string | null>(null)
  const [actionError, setActionError] = useState<string | null>(null)

  const reload = useCallback(() => {
    if (!creatorId) return
    fetchNotableHistory(creatorId)
      .then(setEntries)
      .catch(() => setEntries([])) // 수동 항목 조회 실패가 자동 이력 표시를 막지 않는다
  }, [creatorId])

  useEffect(() => reload(), [reload])

  // 백분위 마일스톤이 있을 때만 그 근거를 밝힌다 — 구독자 돌파·수동 항목은 소스가 달라 싸잡지 않는다.
  const hasBrandedPctile = milestones.some((m) => m.code === 'branded_pctile')

  const badgeItems = milestones.filter((m) => !m.year?.trim())
  // 연도 있는 항목(자동 + 레거시 수동 + 포털 수동) 병합 — 정렬 규칙은 historyRows.ts가 단일 원천.
  const yearedRows = mergeHistoryRows(history, entries)

  async function run(action: () => Promise<unknown>) {
    setActionError(null)
    try {
      await action()
      reload()
    } catch (e) {
      setActionError((e as { message?: string })?.message ?? '처리하지 못했습니다.')
      throw e // 편집 폼이 자체 캡션으로도 알리도록 다시 던진다
    }
  }
  const submitNew = async (input: NotableHistoryInput) => {
    await run(() => createNotableHistory(creatorId as string, input))
    setAdding(false)
  }
  const submitEdit = (id: string) => async (input: NotableHistoryInput) => {
    await run(() => updateNotableHistory(creatorId as string, id, input))
    setEditId(null)
  }

  const isEmpty = milestones.length === 0 && manual.length === 0 && entries.length === 0

  return (
    <div className="min-w-0 border-b border-line-hairline p-[26px_28px] md:border-b-0 md:border-r">
      <SectionHeader
        label="NOTABLE HISTORY · 활동 이력"
        note={
          creatorId ? (
            <button
              type="button"
              onClick={() => {
                setEditing(editing === 'on' ? 'off' : 'on')
                setAdding(false)
                setEditId(null)
                setActionError(null)
              }}
              className="inline-flex items-center gap-1 border border-line-soft px-2 py-0.5 font-text text-[11px] text-fg-2 hover:bg-surface-soft focus:shadow-focus focus:outline-none"
            >
              {editing === 'on' ? (
                <>
                  <X className="h-3 w-3" aria-hidden />
                  편집 종료
                </>
              ) : (
                <>
                  <Pencil className="h-3 w-3" aria-hidden />
                  편집
                </>
              )}
            </button>
          ) : null
        }
      />

      {isEmpty ? (
        <div className="border border-dashed border-line-soft bg-surface-soft p-5 font-text text-[14px] text-fg-3">
          활동 이력이 아직 없습니다.
        </div>
      ) : (
        <div>
          {/* 연도 없는 마일스톤 배지 그룹 */}
          {badgeItems.map((m, i) => (
            <div
              key={`badge-${m.code}-${i}`}
              className="flex gap-4 border-b border-line-hairline py-[11px]"
            >
              <span className="flex flex-none basis-[46px] items-start">
                <span className="whitespace-nowrap border border-brand-blue px-[5px] py-0.5 font-text text-[11px] font-semibold text-brand-blue">
                  {/* 현재값 스냅샷(백분위·누적 브랜드)은 '현재', 임계 돌파형은 '달성' */}
                  {m.code === 'branded_pctile' || m.code === 'brand_count' ? '현재' : '달성'}
                </span>
              </span>
              <div className="min-w-0 flex-1 break-keep font-text text-[14px] leading-[1.5] text-fg-1">
                {m.label}
                {/* 누적 협업 브랜드는 그 채널의 협업 브랜드 수 파생이라 **채널마다 계단이 갈린다**
                    (BE addBrandCountMilestone ← collaborationBrands.size()). 합본이 채널별로 한 줄씩
                    남기므로, 출처를 안 적으면 "30+"와 "10+"가 왜 같이 있는지 읽을 수 없다.
                    나머지 마일스톤(구독자·최고 조회·백분위·협업 연차)은 플랫폼 무관이라 안 붙인다. */}
                {m.sourcePlatform && (
                  <span className="ml-1.5 whitespace-nowrap font-text text-[11px] text-fg-3">
                    · {PLATFORM_LABEL[m.sourcePlatform]} 기준
                    {/* 브랜드 목록이 AI 판독이면 이 계단도 AI 판독 결과다 — 계단만 사실인 척하면 안 된다. */}
                    {m.sourcePlatform !== 'youtube' && ' · AI 판독'}
                  </span>
                )}
                {/* 백분위 마일스톤은 BE 모수 30 가드 + 화면 n<5 캡션의 이중 가드다. 모수를 드러내야
                    어느 가드가 이겼는지 화면에서 검증된다(모수 미도달이면 캡션 없음 = 현행).
                    값 자리의 1은 "마일스톤이 떴다 = 값이 있다"는 사실 — 캡션은 모수만 말한다. */}
                {m.sampleSize != null && (
                  <div className="mt-1">
                    <MetricCaption
                      status={resolveMetric({ value: 1, sampleSize: m.sampleSize })}
                    />
                  </div>
                )}
              </div>
            </div>
          ))}

          {/* 연도 있는 항목(자동 + 레거시 수동 + 포털 수동) 내림차순 */}
          {yearedRows.map((row, i) => (
            <div key={row.key}>
              <div
                className={[
                  'flex items-start gap-4 py-[11px]',
                  i === yearedRows.length - 1 && editId !== row.entryId
                    ? ''
                    : 'border-b border-line-hairline',
                ].join(' ')}
              >
                <span className="flex-none basis-[46px] font-display text-[14px] font-bold tabular-nums text-brand-blue">
                  {row.year}
                </span>
                <div className="min-w-0 flex-1 break-keep font-text text-[14px] leading-[1.5] text-fg-1">
                  {row.text}
                </div>
                {/* 편집 모드에서 포털 수동 항목만 조작 가능 — 자동·레거시는 원천이 달라 여기서 못 고친다 */}
                {editing === 'on' && row.entryId && (
                  <div className="flex flex-none items-center gap-1">
                    <button
                      type="button"
                      onClick={() => {
                        setEditId(editId === row.entryId ? null : (row.entryId as string))
                        setAdding(false)
                      }}
                      aria-label="수정"
                      className="p-1 text-fg-3 hover:text-fg-1 focus:shadow-focus focus:outline-none"
                    >
                      <Pencil className="h-3.5 w-3.5" aria-hidden />
                    </button>
                    <button
                      type="button"
                      onClick={() => void run(() => deleteNotableHistory(creatorId as string, row.entryId as string)).catch(() => {})}
                      aria-label="삭제"
                      className="p-1 text-fg-3 hover:text-status-danger focus:shadow-focus focus:outline-none"
                    >
                      <Trash2 className="h-3.5 w-3.5" aria-hidden />
                    </button>
                  </div>
                )}
              </div>
              {editId && editId === row.entryId && (
                <NotableHistoryEditor
                  initial={{ year: row.year, note: row.text }}
                  submitLabel="수정"
                  onSubmit={submitEdit(row.entryId)}
                  onCancel={() => setEditId(null)}
                />
              )}
            </div>
          ))}
        </div>
      )}

      {/* 백분위 마일스톤 근거 — 블록당 1회 집약(행마다 붙이지 않는다) */}
      {hasBrandedPctile && (
        <p className="mt-3 break-keep font-text text-[11px] leading-[1.5] text-fg-3">
          {buildBasisNote('브랜디드 백분위 · 숏폼 협찬 영상', meta)}
        </p>
      )}

      {/* 수동 항목 추가 — 편집 모드에서만 노출(읽기 화면에 입력 UI를 상시로 두지 않는다) */}
      {editing === 'on' && creatorId && (
        <div className="mt-4">
          {adding ? (
            <NotableHistoryEditor
              submitLabel="추가"
              onSubmit={submitNew}
              onCancel={() => setAdding(false)}
            />
          ) : (
            <button
              type="button"
              onClick={() => {
                setAdding(true)
                setEditId(null)
              }}
              className="inline-flex items-center gap-2 border border-line-soft px-4 py-2 font-text text-[12px] font-medium text-fg-2 hover:bg-surface-soft focus:shadow-focus focus:outline-none"
            >
              <Plus className="h-3.5 w-3.5" aria-hidden />
              수동 항목 추가
            </button>
          )}
          {actionError && (
            <p className="mt-2 break-keep font-text text-[11px] text-status-danger">{actionError}</p>
          )}
          <p className="mt-2 font-text text-[11px] text-fg-3">
            수동 항목은 이 워크스페이스에만 저장됩니다(브랜드 소개서에는 포함되지 않습니다).
          </p>
        </div>
      )}
    </div>
  )
}
